// Shared fork-into-a-song flow. Forking is a two-step exchange: the
// worker creates the copy + lineage row and returns the new patch id,
// then we read that patch back for its title (with the " (fork)"
// suffix) and fresh code so the caller can seed a local Song.
//
// Both the community feed (CommunityPatches) and the shared-song banner
// (SharedSongBanner) drive the same flow, so it lives here once.
//
// The flow also stamps a `// forked from …` attribution comment onto
// the fork's first line — credit that travels with the code itself
// (the blocks parser keeps leading comments as comment statements).
// It's written to the fork's cloud row, not just the returned code:
// App's activeSong watcher reloads the editor buffer from the row
// after the library refetch, so a buffer-only stamp would be wiped.

import { getSupabase } from '../auth/client';
import { buildShareUrl } from '../share/route';
import { forkPatch, getCloudPatchCode } from './forkPatch';

export type ForkSource = {
  title: string;
  authorHandle: string;
};

export type ForkedSong = {
  title: string;
  code: string;
  cloudPatchId: string;
  /** True when the lineage row could not be written — the fork itself
   *  succeeded, so the caller should warn rather than error. */
  lineageMissing?: boolean;
};

export function forkAttributionComment(
  sourcePatchId: string,
  source: ForkSource,
): string {
  return `// forked from "${source.title}" by @${source.authorHandle} — ${buildShareUrl(sourcePatchId)}`;
}

export async function forkPatchToSong(
  sourcePatchId: string,
  source: ForkSource,
): Promise<ForkedSong> {
  const result = await forkPatch(sourcePatchId);
  const fork = await getCloudPatchCode(result.forkPatchId);
  if (!fork) throw new Error('fork created but read-back failed');
  const code = `${forkAttributionComment(sourcePatchId, source)}\n${fork.code}`;
  await persistForkCode(result.forkPatchId, code);
  return {
    title: fork.title,
    code,
    cloudPatchId: result.forkPatchId,
    lineageMissing: result.lineageMissing,
  };
}

// The credit is decorative — if the write fails the fork still exists
// with the original code, so we swallow the error rather than turning
// a successful fork into a user-facing failure.
async function persistForkCode(
  forkPatchId: string,
  code: string,
): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  try {
    await supabase.from('patches').update({ code }).eq('id', forkPatchId);
  } catch {
    // Swallow — see note above.
  }
}
